import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  untracked,
} from '@angular/core';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { DividerModule } from 'primeng/divider';
import { PrimeTemplate } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { AlertComponent } from '../../atoms/alert/alert.component';
import {
  addById,
  haveSameValues,
  removeByIds,
} from '../../../utility/array.utility';

/**
 * Pick list for bookmark categories backed by the `BookmarkCategoryStore` selection.
 *
 * The store selection is the single source of truth, both lists are derived from it.
 * `selectedIds` is taken over as soon as the store has loaded its items.
 */
import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-bookmark-category-pick-list',
  standalone: true,
  imports: [
    DividerModule,
    PickListModule,
    PrimeTemplate,
    TagModule,
    AlertComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './bookmark-category-pick-list.component.html',
  styleUrl: './bookmark-category-pick-list.component.scss',
})
export class BookmarkCategoryPickListComponent implements OnDestroy {
  /** Preselected ids; without it the current store selection is used. */
  readonly selectedIds = input<string[]>();
  @Input() keepSelection = false;
  @Input() showSourceControls = false;
  /** Reordering the target is not persisted, so the controls are hidden by default. */
  @Input() showTargetControls = false;
  @Output() selectionChanged = new EventEmitter<string[]>();

  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);

  private readonly selectedIdSet = computed(
    () => new Set(this.bookmarkCategoryStore.selection().map(item => item._id))
  );

  /** Items which are not selected yet. */
  readonly sourceList = computed(() => {
    const selected = this.selectedIdSet();
    return this.bookmarkCategoryStore
      .asArray()
      .filter((category: BookmarkCategory) => !selected.has(category._id));
  });

  readonly targetList = computed(() => [
    ...this.bookmarkCategoryStore.selection(),
  ]);

  constructor() {
    effect(
      () => {
        const ids = this.selectedIds();
        this.bookmarkCategoryStore.items();
        if (!ids) return;
        untracked(() => {
          if (!haveSameValues(ids, [...this.selectedIdSet()])) {
            this.bookmarkCategoryStore.selectMultiple(ids);
          }
        });
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Adds the moved items to the store selection and emits the new selection.
   *
   * @param {PickListMoveToTargetEvent} event - Contains the moved items.
   */
  addToSelection(event: PickListMoveToTargetEvent): void {
    this.bookmarkCategoryStore.updateSelection(
      addById(
        this.bookmarkCategoryStore.selection(),
        event.items as BookmarkCategory[]
      )
    );
    this.emitSelection();
  }

  /**
   * Removes the moved items from the store selection and emits the new selection.
   *
   * @param {PickListMoveToSourceEvent} event - Contains the moved items.
   */
  removeFromSelection(event: PickListMoveToSourceEvent): void {
    this.bookmarkCategoryStore.updateSelection(
      removeByIds(
        this.bookmarkCategoryStore.selection(),
        (event.items as BookmarkCategory[]).map(item => item._id)
      )
    );
    this.emitSelection();
  }

  private emitSelection(): void {
    this.selectionChanged.emit(
      this.bookmarkCategoryStore.selection().map(item => item._id)
    );
  }

  /**
   * Clears the selection on destroy unless `keepSelection` is set.
   */
  ngOnDestroy(): void {
    if (this.keepSelection) return;
    this.bookmarkCategoryStore.clearSelection();
  }
}
